import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, ArrowLeftIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import AdminHeader from '../../../shared/components/AdminHeader';
import Pagination from '../../../shared/components/Pagination';
import Spinner from '../../../shared/components/Spinner';
import { useAuth } from '../../../core/providers/AuthProvider';
import { ROLES } from '../../../shared/constant/roles';
import { GRADE_LEVELS, getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import { confirmAction, showAlert, showSuccessToast } from '../../../shared/utils/dialogs';
import { createSubject, deleteSubject, getSubjects, updateSubject } from '../services/subjectService';
import SubjectFormModal from './SubjectFormModal';

const PAGE_SIZE = 10;
const CATEGORIES = ['Core', 'Applied', 'Specialized'];

function SubjectsPage() {
  const { role } = useAuth();
  const isTeacherRole = role === ROLES.TEACHER;
  const [subjects, setSubjects] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingSubject, setEditingSubject] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadSubjects = useCallback(async () => {
    setLoading(true);
    try {
      const { data, count } = await getSubjects({ search, category, gradeLevel, page, pageSize: PAGE_SIZE });
      setSubjects(data);
      setTotalCount(count);
    } catch (err) {
      showAlert({ text: err.message || 'Failed to load subjects' });
    } finally {
      setLoading(false);
    }
  }, [search, category, gradeLevel, page]);

  useEffect(() => {
    loadSubjects();
  }, [loadSubjects]);

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const hasActiveFilters = Boolean(search || category || gradeLevel);

  const handleClearFilters = () => {
    setSearch('');
    setCategory('');
    setGradeLevel('');
    setPage(1);
  };

  const openAddForm = () => {
    setEditingSubject(null);
    setIsFormOpen(true);
  };

  const openEditForm = (subject) => {
    setEditingSubject(subject);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (values) => {
    if (editingSubject) {
      await updateSubject(editingSubject.id, values);
      showSuccessToast('Subject updated');
    } else {
      await createSubject(values);
      showSuccessToast('Subject added');
    }
    setIsFormOpen(false);
    setEditingSubject(null);
    loadSubjects();
  };

  const handleDelete = async (subject) => {
    const confirmed = await confirmAction({
      title: 'Remove subject?',
      text: `${subject.name} and any grades recorded for it will be removed.`,
      confirmText: 'Remove',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await deleteSubject(subject.id, subject.name);
      showSuccessToast('Subject removed');
      loadSubjects();
    } catch (err) {
      showAlert({ text: err.message || 'Failed to remove subject' });
    }
  };

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <AdminHeader />

      <main className="flex-1 min-h-0 w-full p-4 lg:w-4/5 lg:mx-auto flex flex-col space-y-4">
        <div className="shrink-0 flex items-center gap-2">
          <Link to="/dashboard" aria-label="Back to dashboard" className="text-[#0b4a71] hover:text-[#3f6d8e]">
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-semibold text-[#0b4a71]">Subjects</h1>
        </div>

        <div className="shrink-0 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <div className="flex gap-2 w-full sm:contents">
              <div className="relative w-full sm:max-w-xs">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search subjects..."
                  value={search}
                  onChange={(e) => handleFilterChange(setSearch)(e.target.value)}
                  className="w-full rounded-md border border-[#4ea6d0] pl-9 pr-3 py-2 lg:text-sm focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                />
              </div>

              <button
                type="button"
                onClick={() => setFiltersOpen((open) => !open)}
                aria-label="Toggle filters"
                className={`sm:hidden shrink-0 rounded-md border border-[#4ea6d0] p-2 ${
                  filtersOpen ? 'bg-[#4ea6d0] text-white' : 'text-[#0b4a71] hover:bg-gray-50'
                }`}
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
            </div>

            <div className={`${filtersOpen ? 'flex' : 'hidden'} sm:flex flex-col sm:flex-row gap-3 w-full`}>
              <div className="flex gap-3 w-full sm:contents">
                <select
                  value={category}
                  onChange={(e) => handleFilterChange(setCategory)(e.target.value)}
                  className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>

                <select
                  value={gradeLevel}
                  onChange={(e) => handleFilterChange(setGradeLevel)(e.target.value)}
                  className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
                >
                  <option value="">All Grade Levels</option>
                  {GRADE_LEVELS.map((g) => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                className="flex items-center justify-center gap-1.5 rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 shrink-0"
              >
                <XMarkIcon className="h-4 w-4" />
                Clear Filter
              </button>
            </div>
          </div>

          {!isTeacherRole && (
            <button
              type="button"
              onClick={openAddForm}
              className="flex items-center justify-center gap-2 rounded-md border border-[#4ea6d0] bg-[#0b4a71] px-4 py-2 lg:text-sm font-medium text-white hover:bg-[#3f6d8e] shrink-0"
            >
              <PlusIcon className="h-4 w-4" />
              Add Subject
            </button>
          )}
        </div>

        <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
          {loading ? (
            <Spinner />
          ) : subjects.length === 0 ? (
            <p className="text-center text-gray-500 py-12">No subjects found.</p>
          ) : (
            <>
              <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#4ea6d0] text-left text-white sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Category</th>
                      <th className="px-4 py-3 font-medium">Grade Level</th>
                      <th className="px-4 py-3 font-medium">Sections</th>
                      <th className="px-4 py-3 font-medium">Teacher</th>
                      {!isTeacherRole && <th className="px-4 py-3 font-medium text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {subjects.map((subject) => (
                      <tr key={subject.id} className="hover:bg-[#4ea6d0]/20">
                        <td className="px-4 py-3 font-medium text-[#0b4a71]">{subject.name}</td>
                        <td className="px-4 py-3 text-gray-600">{subject.category}</td>
                        <td className="px-4 py-3 text-gray-600">{subject.grade_level ? getGradeLevelLabel(subject.grade_level) : '—'}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {subject.subject_sections?.length
                            ? subject.subject_sections.map((s) => s.section).sort().join(', ')
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {subject.teachers
                            ? `${subject.teachers.firstname} ${subject.teachers.lastname}`
                            : '—'}
                        </td>
                        
                        {!isTeacherRole && (
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEditForm(subject)}
                                aria-label="Edit subject"
                                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-[#0b4a71]"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(subject)}
                                aria-label="Remove subject"
                                className="p-1.5 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </main>

      {isFormOpen && (
        <SubjectFormModal
          subject={editingSubject}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
}

export default SubjectsPage;
